import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

const IMPERSONATION_SESSION_STORAGE_KEY = "intellsys:impersonation-session";

const impersonationSessionValidator = zod.object({
    accessToken: zod.string().min(1),
    expiresAt: zod.string().datetime(),
    impersonationSessionId: uuidZodValidator,
    targetUserName: zod.string().min(1).optional(),
});

export type ImpersonationSession = {
    accessToken: string;
    expiresAt: string;
    impersonationSessionId: Uuid;
    targetUserName: string;
};

/**
 * Deletes the temporary tab-scoped impersonation token. The primary Heimdall
 * session remains in local storage and is never changed by this utility.
 */
export function clearImpersonationSession(): void {
    sessionStorage.removeItem(IMPERSONATION_SESSION_STORAGE_KEY);
}

/**
 * Returns the valid, tab-scoped impersonation session, clearing malformed or
 * expired values before they can be used for authenticated requests.
 */
export function getImpersonationSession(): ImpersonationSession | null {
    const storedValue = sessionStorage.getItem(IMPERSONATION_SESSION_STORAGE_KEY);
    let rawStoredValue: unknown;
    try {
        rawStoredValue = storedValue === null ? null : JSON.parse(storedValue);
    } catch {
        clearImpersonationSession();
        return null;
    }
    const parsedValue = impersonationSessionValidator.safeParse(rawStoredValue);
    if (parsedValue.success !== true || new Date(parsedValue.data.expiresAt).getTime() <= Date.now()) {
        clearImpersonationSession();
        return null;
    }

    return {
        accessToken: parsedValue.data.accessToken,
        expiresAt: parsedValue.data.expiresAt,
        impersonationSessionId: parsedValue.data.impersonationSessionId,
        targetUserName: parsedValue.data.targetUserName ?? "the selected user",
    };
}

/**
 * Stores the temporary real OIDC access token in session storage so it is
 * limited to the current browser tab and disappears when the tab closes.
 */
export function setImpersonationSession({
    accessToken,
    expiresInSeconds,
    impersonationSessionId,
    targetUserName,
}: {
    accessToken: string;
    expiresInSeconds: number;
    impersonationSessionId: Uuid;
    targetUserName: string;
}): void {
    const session: ImpersonationSession = {
        accessToken: accessToken,
        expiresAt: new Date(Date.now() + expiresInSeconds * 1000).toISOString(),
        impersonationSessionId: impersonationSessionId,
        targetUserName: targetUserName,
    };
    restoreImpersonationSession(session);
}

export function restoreImpersonationSession(session: ImpersonationSession): void {
    sessionStorage.setItem(IMPERSONATION_SESSION_STORAGE_KEY, JSON.stringify(session));
}

/**
 * Adds the target user's display name to an existing impersonation session.
 * This lets sessions created before the display name was stored show the
 * actual user once their profile has been loaded.
 */
export function setImpersonationSessionTargetUserName({targetUserName}: {targetUserName: string}): void {
    const normalizedTargetUserName = targetUserName.trim();
    if (normalizedTargetUserName.length === 0) {
        return;
    }

    const session = getImpersonationSession();
    if (session === null) {
        return;
    }

    restoreImpersonationSession({...session, targetUserName: normalizedTargetUserName});
}
